import React from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Grid } from '@opendoor/atoms/grid';

import { cssVars } from '../helpers/cssVars';

interface TableProps {
  children: React.ReactNode;
  columns?: string; // e.g., "1fr 2fr 1fr" or "repeat(3, 1fr)"
}

/**
 * Table component using CSS Grid.
 *
 * `columns` is runtime-dynamic and Atoms strips the inline `style` prop, so it
 * reaches the DOM as the `--table-columns` custom property and is consumed by
 * the `grid-cols-(--table-columns)` utility.
 *
 * @example
 * ```tsx
 * <Table columns="1fr 2fr 1fr">
 *   <TableRow>
 *     <TableCell>Cell 1</TableCell>
 *     <TableCell>Cell 2</TableCell>
 *     <TableCell>Cell 3</TableCell>
 *   </TableRow>
 * </Table>
 * ```
 */
export const Table: React.FC<TableProps> = ({ children, columns = 'auto' }) => {
  return (
    <Grid
      ref={cssVars<HTMLElement>({ '--table-columns': columns })}
      className="w-full grid-cols-(--table-columns) border border-od-border-secondary"
    >
      {children}
    </Grid>
  );
};

interface TableRowProps {
  children: React.ReactNode;
}

/**
 * TableRow component. Uses display:contents so its cells participate directly
 * in the Table's grid. For clickable rows, use PressableTableRow instead.
 */
export const TableRow: React.FC<TableRowProps> = ({ children }) => {
  return <Col className="contents">{children}</Col>;
};

interface PressableTableRowProps {
  children: React.ReactNode;
  /** Required: the row's cells are its only content, so it has no other accessible name. */
  'aria-label': string;
  analyticsName?: string;
  onClick: () => void;
}

/**
 * Clickable table row. Like TableRow it is display:contents, so its cells
 * participate directly in the Table's grid.
 */
export const PressableTableRow: React.FC<PressableTableRowProps> = ({
  children,
  'aria-label': ariaLabel,
  analyticsName,
  onClick,
}) => {
  return (
    <Col
      onClick={onClick}
      aria-label={ariaLabel}
      analyticsName={analyticsName}
      className="contents cursor-pointer"
    >
      {children}
    </Col>
  );
};

interface TableCellProps {
  children: React.ReactNode;
}

/** Grid cell with borders and padding. */
export const TableCell: React.FC<TableCellProps> = ({ children }) => {
  return <Row className="items-center border border-od-border-secondary p-1.5">{children}</Row>;
};

interface TableHeaderCellProps {
  children: React.ReactNode;
}

/** Grid cell with bold text and header styling. */
export const TableHeaderCell: React.FC<TableHeaderCellProps> = ({ children }) => {
  return (
    <Row className="items-center border border-od-border-secondary p-1.5 font-bold">{children}</Row>
  );
};
